import React, { useState } from "react";
import { useEffectOnce } from "@lib-shared/hooks";
import { cleanUpHtml, logApiError } from "core/lib";
import UrlUtils from "core/lib/url-utils";
import { FreshDeskService } from "core/services/fresh-desk.service";
import { Ticket, TicketComment } from "core/types/ticket";
import Comment from "./comment";
import { ModalAlert, PleaseWait } from "components/common-ui";
import NewCommentForm from "./new-comment-form";
import useLocalDateTime from "../use-locale-datetime";

type QueryDetailsProps = {
  auth: any,
  id: string,
  onClose: any
}

const QueryDetails = (props: QueryDetailsProps) => {
  const { auth, id, onClose } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const [queryDetails, setQueryDetails] = useState<Ticket>();
  const [comments, setComments] = useState<TicketComment[]>([]);

  const api = new FreshDeskService();

  const locale = UrlUtils.getLanguageCode();

  const [getTimeString, getDateString] = useLocalDateTime(locale);

  const usersFirstName = (auth.name ?? "").split(" ")[0];

  const handleError = (message: string, error: any) => {
    logApiError(error);

    ModalAlert.error(message);
  }

  //getting data
  const loadTicketHandlerAsync = async () => {
    try {
      const response = await api.getTicketAsync(id);

      if (response.data) {
        const ticket = processGetTicketResponse(response.data);
        ticket.user_name = usersFirstName;

        setQueryDetails(ticket);
      }

      setIsLoading(false);

    } catch (error) {
      setIsLoading(false);

      handleError("Failed to load the query.", error);
    }
  }

  const processGetTicketResponse = (ticket: Ticket) => {
    ticket.description = cleanUpHtml(ticket.description);
    return ticket;
  };

  const moveToBottomComment = () => {
    const conversationUl = document.querySelector(".conversation") as HTMLElement

    if (!conversationUl) {
      return false;
    }

    conversationUl.scrollTop = conversationUl.scrollHeight;
    return true;
  }

  const timeOut = 500;

  const moveToBottomCommentAfterTimeout = () => {
    if (moveToBottomComment()) {
      return;
    }

    setTimeout(moveToBottomCommentAfterTimeout, timeOut);
  }

  const loadCommentsAsync = async () => {
    setIsLoadingComments(true);

    try {
      const response = await api.getTicketConversationsAsync(id ?? "");
      const data = response?.data;

      if (data?.length > 0) {
        setComments(processConversationResponse(data));
      }

      setIsLoadingComments(false);


      setTimeout(moveToBottomCommentAfterTimeout, timeOut);

    } catch (error) {
      setIsLoadingComments(false);
      handleError("Failed to load feedback.", error);
      return;
    }
  }

  const processConversationResponse = (feedback: TicketComment[]) => {
    const comments: TicketComment[] = [];

    feedback.forEach((comment: TicketComment) => {
      //Api Docs: Incoming = Set to true if a particular conversation should appear as being created from outside 
      //(i.e., not through web portal)
      //So false if created via ClientApp
      const isUsersComment = comment.user_id?.toString() === auth.id.toString();

      comment.body = cleanUpHtml(comment.body);
      comment.incoming = !isUsersComment;// ? false : comment.incoming;

      comment.user_name = isUsersComment ? usersFirstName : comment.support_email;

      comment.date = getDateString(comment.created_at, true);
      comment.time = getTimeString(comment.created_at);

      comments.push(comment);
    });

    return comments;
  }

  useEffectOnce(() => {
    const fetchData = async () => {
      setIsLoading(true);

      await loadTicketHandlerAsync();
      await loadCommentsAsync();
    }

    fetchData();
  });

  const handleCommentSubmitAsync = async (data: any) => {
    try {
      const response = await api.createReplyAsync(id ?? "", data);

      if (response?.data) {
        setIsLoadingComments(true);
        await loadCommentsAsync();
        setIsLoadingComments(false);
        return true;
      }
    } catch (error) {
      setIsLoadingComments(false);
      logApiError(error);
      return false;
    }

    return false;
  }

  const Conversation = () => {
    if (!queryDetails) {
      return null;
    }

    if (isLoadingComments) {
      return <PleaseWait message="Loading comments" title={""} />;
    }

    queryDetails.time = getTimeString(queryDetails.created_at);
    queryDetails.date = getDateString(queryDetails.created_at, true);

    return (<ul className="conversation">
      <Comment comment={queryDetails} />

      {comments.map((comment, index) => {
        return <Comment key={index} comment={comment} previousComment={index === 0 ? undefined : comments[index = 1]} />;
      })}
    </ul>);
  }

  if (isLoading) {
    return (
      <PleaseWait message="Getting query details" isInModal={true} />
    );
  }

  if (!queryDetails) {
    return null;
  }

  return (
    <section className="query-details">
      <section className="details">
        <h3>Query Details</h3>

        <i className="icon icon-close" onClick={onClose}></i>

        <dl>
          <dt>{queryDetails.subject}</dt>
          <dd>Reference: {queryDetails.id}</dd>

          <dt>Category</dt>
          <dd>{queryDetails.category}</dd>

          <dt>Date</dt>
          <dd>{getDateString(queryDetails.created_at)}</dd>

          <dt>Status</dt>
          <dd className={`status-${queryDetails.status?.toString()?.toLowerCase()}`}>{queryDetails.status}</dd>

          <dt>Submitted By</dt>
          <dd>{queryDetails.createdBy}</dd>

          <dt>Channel</dt>
          <dd>{queryDetails.source}</dd>
        </dl>
      </section>

      <section className="conversation">
        <h3>Conversation</h3>

        <Conversation />
      </section>

      <section className="add-comment">
        <h3>Add a comment</h3>

        <NewCommentForm
          auth={auth}
          display={true}
          onSubmit={async (data: any) => await handleCommentSubmitAsync(data)}
          onClose={onClose} />
      </section>
    </section>
  );
}

export default QueryDetails;
